import React from "react";
import { Button } from "@/components/ui/button";
import { ExternalLink, AlertCircle } from "lucide-react";
import { PROVIDERS } from "@/lib/booking";
import { useAvailabilityPing } from "@/hooks/useAvailabilityPing";

/**
 * Renders provider buttons in preferred-first order, with a best-effort
 * availability ping on the primary one. If the ping suggests "no availability",
 * the alternate provider button gets an amber highlight and a "Try instead" hint.
 * If no preferred provider is set, both buttons are equal peers.
 */
export default function ProviderButtons({ ctx, preferredId, variant = "outline",
                                          onOpen, testidSuffix = "" }) {
  const ordered = orderedProviders(preferredId);
  const primary = ordered[0];
  const primaryUrl = primary?.build(ctx);
  const ping = useAvailabilityPing({ provider: primary?.id, url: primaryUrl,
                                     enabled: !!primary && !!primaryUrl });

  const suggestAlt = ping.state === "likely_full";

  return (
    <div className="w-full space-y-2" data-testid={`provider-buttons${testidSuffix}`}>
      <div className="flex gap-2 flex-wrap">
        {ordered.map((p, idx) => {
          const url = p.build(ctx);
          const isPrimary = idx === 0;
          const isSuggested = idx === 1 && suggestAlt;
          const style = isSuggested
            ? "default"
            : (isPrimary && preferredId === p.id ? "default" : variant);
          return (
            <Button
              key={p.id}
              variant={style}
              size="sm"
              className={`flex-1 ${isSuggested ? "ring-2 ring-amber-400 animate-pulse" : ""}`}
              data-testid={`book-${p.id}-btn${testidSuffix}`}
              onClick={() => onOpen(p, url)}
            >
              <ExternalLink className="w-3 h-3 mr-1"/>
              Book on {p.label}
              {isPrimary && preferredId === p.id && !suggestAlt && (
                <span className="ml-1 text-[10px]">· usual</span>
              )}
              {isSuggested && <span className="ml-1 text-[10px]">· try this</span>}
            </Button>
          );
        })}
      </div>
      {suggestAlt && ordered.length > 1 && (
        <div className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1" data-testid="provider-fallback-hint">
          <AlertCircle className="w-3 h-3 shrink-0"/>
          Looks like <strong>{primary.label}</strong> may be full for this time —
          try <strong>{ordered[1].label}</strong> instead.
        </div>
      )}
      {ping.state === "loading" && (
        <div className="text-[10px] text-muted-foreground mono" data-testid="provider-ping-loading">
          Checking {primary?.label} availability…
        </div>
      )}
    </div>
  );
}

function orderedProviders(preferredId) {
  if (!preferredId) return PROVIDERS;
  const preferred = PROVIDERS.find(p => p.id === preferredId);
  if (!preferred) return PROVIDERS;
  return [preferred, ...PROVIDERS.filter(p => p.id !== preferredId)];
}
